import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useGridApiRef, gridFilteredSortedRowEntriesSelector } from '@mui/x-data-grid-pro';
import Datagrid from '../ui/Datagrid';
import columns from './table_config';
import { Box, Dialog, DialogActions, DialogTitle, Grid, IconButton, gridClasses } from '@mui/material';
import { renderCell } from '../ui/Datagrid/table_config';
import PropTypes from 'prop-types';
import { deleteAssetType, listAssetTypes, updateAssetType } from '../../store/actions/assetTypes';
import { SaveRounded, EditOutlined, DeleteRounded, WarningAmberRounded } from '@mui/icons-material';
import { REDUX_ACTIONS } from '../../shared/config/constants';
import ReactJson from 'react-json-view';
import AssetDetails from '../assetDetails';
import { enqueueSnackbar } from '../../store/actions/snackbar';
import ButtonComponent from '../Button';
import SelectEditInputTextField from '../ui/SelectEditInputTextField';

const AssetTypes = () => {
  const dispatch = useDispatch();
  const apiRef = useGridApiRef();
  const [editDialog, setEditDialog] = useState(false);
  const [editData, setEditData] = useState({});
  const [pageSize, setPageSize] = React.useState(5);
  const [confirmDelete, setConfirmDelete] = useState({
    open: false,
    data: null
  });
  const assetTypes = useSelector((state) => state.assetTypes);

  useEffect(() => {
    dispatch(listAssetTypes());
    return () => {
      dispatch({
        type: REDUX_ACTIONS.ASSETTYPE_RESET
      });
    };
  }, []);

  useEffect(() => {
    if (assetTypes?.data[0]?.details?.name === '') {
      const id = assetTypes?.data[0]._id;
      setTimeout(() => {
        apiRef.current.scrollToIndexes({
          rowIndex: 1
        });
        apiRef.current.setCellFocus(id, 'name');
        apiRef.current.setEditCellValue({ id, field: 'name', value: '' }); // starts edit mode
        setTimeout(() => {
          apiRef.current.setCellFocus(id, 'name');
        }, 50);
      }, 50);
    }
  }, [assetTypes.data.length]);

  const handleSave = (row) => {
    if (!row?.details?.name) {
      dispatch(
        enqueueSnackbar(
          'Please enter asset type name',
          'error',
          new Date().getTime() + Math.random()
        )
      );
      return;
    }
    if (Object.keys(row?.details?.properties)?.length === 0) {
      dispatch(
        enqueueSnackbar(
          'Please add asset type details',
          'error',
          new Date().getTime() + Math.random()
        )
      );
      return;
    }
    const body = {
      _id: row?._id,
      details: {
        name: row?.details?.name,
        properties: row?.details?.properties
      }
    };
    dispatch(updateAssetType(body));
  };

  const handleDelete = () => {
    dispatch(deleteAssetType({ _id: confirmDelete.data._id }, () => {
      setConfirmDelete({
        open: false,
        data: null
      });
    }));
  };

  const renderActionsCell = ({ row }) => {
    return (
      <Grid container justifyContent="center">
        <IconButton
          variant="solid"
          onClick={() => {
            setEditData(row);
            setEditDialog(true);
          }}
        >
          <EditOutlined />
        </IconButton>
        <IconButton
          variant="solid"
          disabled={!row?.isDirty}
          onClick={() => {
            handleSave(row);
          }}
        >
          <SaveRounded />
        </IconButton>
        <IconButton
          disabled={row?.isNewRow}
          variant="solid"
          onClick={() => {
            setConfirmDelete({
              open: true,
              data: row
            });
          }}
        >
          <DeleteRounded />
        </IconButton>
      </Grid>
    );
  };

  const RenderJsonField = ({ value }) => {
    const data = useRef(value);

    return (
      <ReactJson
        src={data.current}
        name={false}
        sortKeys={true}
        collapsed={false}
        displayDataTypes={false}
        quotesOnKeys={false}
      />
    );
  };

  const updateData = (id, field, value) => {
    const updatedData = assetTypes.data.map((row) => {
      if (row._id === id) {
        const updatedValue = typeof value === 'string' ? value : Number(value);
        const isDirty = value !== row[field];
        return {
          ...row,
          details: {
            ...row.details,
            [field]: updatedValue
          },
          isDirty: row.isDirty || isDirty,
        };
      }
      return row;
    });
    dispatch({
      type: REDUX_ACTIONS.ASSETTYPE_LIST_SUCCESS,
      payload: {
        data: updatedData,
        paginatedElements: updatedData?.length,
      },
    });
  };

  const renderValueCell = (params) => {
    return <SelectEditInputTextField {...params} updateData={updateData} />;
  };

  return (
    <Grid container justifyContent="center">
      <Grid item xs={12}>
        <Datagrid
          apiRef={apiRef}
          data={assetTypes.data}
          getRowId={(row) => row._id}
          columns={columns(renderCell, RenderJsonField, renderActionsCell, renderValueCell)}
          loading={assetTypes.loading}
          pageSize={pageSize}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          rowsPerPageOptions={[5, 10, 20, 50, 100]}
          pagination
          toolbar={{
            options: {
              columns: true,
              filters: true,
              export: false,
            },
          }}
          getRowHeight={() => 'auto'}
          sx={{
            '.MuiDataGrid-columnHeaderTitle': {
              fontWeight: 'bold !important',
            },
            [`& .${gridClasses.cell}`]: {
              py: 1,
            },
          }}
          height={'90vh'}
          onFilterModelChange={() => {
            setTimeout(() => {
              const filterData = gridFilteredSortedRowEntriesSelector(apiRef.current.state, apiRef.current.instanceId).map(item => {
                return { ...item.model };
              });

              if (filterData) {
                dispatch({
                  type: REDUX_ACTIONS.ASSETTYPE_FILTER,
                  payload: {
                    data: filterData,
                  }
                });
              }
            }, 300);
          }}
        />
      </Grid>
      {editDialog && editData?.details?.name
        ? (
        <AssetDetails
          open={editDialog}
          handleClose={() => {
            setEditDialog(false);
            setEditData({});
          }}
          data={editData}
        />
          )
        : null}
      {confirmDelete.open
        ? (
        <Dialog
          open={confirmDelete.open}
          onClose={() => {
            setConfirmDelete({
              open: false,
              data: null
            });
          }}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          maxWidth='md'
        >
          <Box
            sx={{ display: 'flex', padding: '20px' }}
          >
            <DialogTitle id="alert-dialog-title" display='flex' alignItems='center' fontSize='20px' fontWeight='bold'>
              <WarningAmberRounded fontSize='large' sx={{ marginBottom: '4px', marginRight: '4px' }} />
              {`Delete ${confirmDelete?.data.name}?`}
            </DialogTitle>
            <DialogActions>
              <ButtonComponent
                label="Cancel"
                size="small"
                onClick={() => {
                  setConfirmDelete({
                    open: false,
                    data: null
                  });
                }}></ButtonComponent>
              <ButtonComponent
              label="Confirm"
              size="small"
                onClick={() => {
                  handleDelete();
                }} autoFocus>
              </ButtonComponent>
            </DialogActions>
          </Box>
        </Dialog>
          )
        : null}
    </Grid>
  );
};

AssetTypes.propTypes = {
  row: PropTypes.object,
  value: PropTypes.object
};

export default AssetTypes;
